import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Edit2, Trash2, CheckCircle, XCircle, Clock,
    Package, Car, Home, Smartphone, Image as ImageIcon,
    Loader2, AlertCircle, User, Building2, Upload, Star,
    FileText, X, ExternalLink
} from 'lucide-react';
import { assetService } from '@services/assetService';

const AssetDetailsView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [asset, setAsset] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Documents state
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    useEffect(() => {
        fetchAssetDetails();
        fetchDocuments();
    }, [id]);

    const fetchAssetDetails = async () => {
        try {
            setLoading(true);
            const response = await assetService.clientAssets.getById(id);
            if (response.success && response.data) {
                setAsset(response.data);
            }
        } catch (error) {
            console.error('Error fetching asset:', error);
            setError('Failed to load asset details');
        } finally {
            setLoading(false);
        }
    };

    const fetchDocuments = async () => {
        try {
            setDocumentsLoading(true);
            const response = await assetService.assetDocuments.getByAsset(id);
            if (response.success && response.data) {
                setDocuments(Array.isArray(response.data) ? response.data : []);
            } else if (Array.isArray(response)) {
                setDocuments(response);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            setDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    };

    const getAssetIcon = (type) => {
        switch (type) {
            case 'VEHICLE': return Car;
            case 'LAND':
            case 'PROPERTY': return Home;
            case 'PHONE': return Smartphone;
            default: return Package;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'VERIFIED': return 'bg-green-100 text-green-800 border-green-200';
            case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200';
            case 'UNDER_REVIEW': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this asset?')) {
            try {
                await assetService.clientAssets.delete(id);
                navigate('/compliance/assets');
            } catch (error) {
                console.error('Error deleting asset:', error);
            }
        }
    };

    const handleVerify = async () => {
        try {
            await assetService.clientAssets.verify(id);
            fetchAssetDetails();
        } catch (error) {
            console.error('Error verifying asset:', error);
        }
    };

    const handleReject = async () => {
        const reason = prompt('Please provide a reason for rejection:');
        if (reason) {
            try {
                await assetService.clientAssets.reject(id, reason);
                fetchAssetDetails();
            } catch (error) {
                console.error('Error rejecting asset:', error);
            }
        }
    };

    const handleSetPrimary = async (docId) => {
        try {
            await assetService.assetDocuments.setPrimary(docId);
            fetchDocuments();
        } catch (error) {
            console.error('Error setting primary document:', error);
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            try {
                await assetService.assetDocuments.delete(docId);
                fetchDocuments();
            } catch (error) {
                console.error('Error deleting document:', error);
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error || !asset) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />
                    <p className="text-gray-900 font-semibold mb-2">Error Loading Asset</p>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/compliance/assets')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Back to Assets
                    </button>
                </div>
            </div>
        );
    }

    const Icon = getAssetIcon(asset.assetType);
    const attributes = asset.attributes ? JSON.parse(asset.attributes) : {};

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/compliance/assets')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-900">Asset Details</h2>
                </div>
            </div>

            {/* Asset Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Icon className="w-6 h-6 text-gray-600" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">{asset.requirementName}</h3>
                            <p className="text-sm text-gray-500">{asset.assetCode}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-sm rounded-full border ${getStatusColor(asset.status)}`}>
                            {asset.status}
                        </span>
                        {asset.status === 'PENDING' && (
                            <>
                                <button
                                    onClick={handleVerify}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    Verify
                                </button>
                                <button
                                    onClick={handleReject}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                                >
                                    <XCircle className="w-4 h-4" />
                                    Reject
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => navigate(`/compliance/assets/${id}/edit`)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                        >
                            <Edit2 className="w-4 h-4" />
                            Edit
                        </button>
                        <button
                            onClick={handleDelete}
                            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition flex items-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete
                        </button>
                    </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Column 1: Client Info */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Client Information</h4>
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs text-gray-500">Client Name</p>
                                <p className="text-sm text-gray-900">{asset.clientName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Client Code</p>
                                <p className="text-sm text-gray-900">{asset.clientCode}</p>
                            </div>
                            <button
                                onClick={() => navigate(`/clients/${asset.clientId}`)}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                View Client →
                            </button>
                        </div>
                    </div>

                    {/* Column 2: Asset Info */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Asset Information</h4>
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs text-gray-500">Asset Type</p>
                                <p className="text-sm text-gray-900">{asset.assetType}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Requirement</p>
                                <p className="text-sm text-gray-900">{asset.requirementName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Documents</p>
                                <p className="text-sm text-gray-900">{documents.length}</p>
                            </div>
                        </div>
                    </div>

                    {/* Column 3: Status Info */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Status Information</h4>
                        <div className="space-y-3">
                            {asset.verifiedAt && (
                                <div>
                                    <p className="text-xs text-gray-500">Verified At</p>
                                    <p className="text-sm text-gray-900">
                                        {new Date(asset.verifiedAt).toLocaleDateString()}
                                    </p>
                                </div>
                            )}
                            {asset.verifiedBy && (
                                <div>
                                    <p className="text-xs text-gray-500">Verified By</p>
                                    <p className="text-sm text-gray-900">{asset.verifiedBy}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-gray-500">Created</p>
                                <p className="text-sm text-gray-900">
                                    {new Date(asset.createdAt).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Asset Attributes */}
                {Object.keys(attributes).length > 0 && (
                    <div className="mt-6 pt-6 border-t">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                            Asset Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(attributes).map(([key, value]) => (
                                <div key={key} className="bg-gray-50 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 mb-1">
                                        {key.replace(/([A-Z])/g, ' $1').replace(/\b\w/g, c => c.toUpperCase()).trim()}
                                    </p>
                                    <p className="text-sm text-gray-900 font-medium">{value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Notes */}
                {asset.notes && (
                    <div className="mt-6 pt-6 border-t">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">
                            Notes
                        </h4>
                        <p className="text-sm text-gray-900">{asset.notes}</p>
                    </div>
                )}

                {/* Rejection Reason */}
                {asset.rejectionReason && (
                    <div className="mt-6 pt-6 border-t">
                        <h4 className="text-sm font-semibold text-red-700 uppercase tracking-wider mb-2">
                            Rejection Reason
                        </h4>
                        <p className="text-sm text-red-900 bg-red-50 p-3 rounded-lg">{asset.rejectionReason}</p>
                    </div>
                )}
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900">Documents</h4>
                        <p className="text-sm text-gray-500">{documents.length} document{documents.length !== 1 ? 's' : ''} uploaded</p>
                    </div>
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    >
                        <Upload className="w-4 h-4" />
                        Upload Document
                    </button>
                </div>

                {documentsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                ) : documents.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No documents uploaded yet</p>
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="mt-3 text-blue-600 hover:text-blue-700 font-medium text-sm"
                        >
                            Upload your first document
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {documents.map((doc) => (
                            <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                                                {DOCUMENT_TYPE_OPTIONS.find(o => o.value === doc.documentType)?.label || doc.documentType || doc.fileName || 'Document'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ''}
                                            </p>
                                        </div>
                                    </div>
                                    {doc.primary && (
                                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
                                            <Star className="w-3 h-3" />
                                            Primary
                                        </span>
                                    )}
                                </div>

                                {doc.description && (
                                    <p className="text-xs text-gray-500 mb-3 truncate">{doc.description}</p>
                                )}

                                {doc.fileUrl && (
                                    <a
                                        href={doc.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mb-3"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                        View File
                                    </a>
                                )}

                                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                                    {!doc.primary && (
                                        <button
                                            onClick={() => handleSetPrimary(doc.id)}
                                            className="text-xs text-gray-600 hover:text-yellow-600 transition flex items-center gap-1"
                                            title="Set as primary"
                                        >
                                            <Star className="w-3 h-3" />
                                            Set Primary
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteDocument(doc.id)}
                                        className="text-xs text-gray-600 hover:text-red-600 transition flex items-center gap-1 ml-auto"
                                        title="Delete document"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Upload Document Modal */}
            {showUploadModal && (
                <UploadDocumentModal
                    assetId={id}
                    assetType={asset.assetType}
                    onClose={() => setShowUploadModal(false)}
                    onSuccess={() => {
                        setShowUploadModal(false);
                        fetchDocuments();
                    }}
                />
            )}
        </div>
    );
};

const DOCUMENT_TYPE_OPTIONS = [
    { value: 'LOGBOOK', label: 'Logbook' },
    { value: 'TITLE_DEED', label: 'Title Deed' },
    { value: 'REGISTRATION', label: 'Registration' },
    { value: 'INSURANCE', label: 'Insurance' },
    { value: 'VALUATION', label: 'Valuation Report' },
    { value: 'PHOTO', label: 'Photo' },
    { value: 'OTHER', label: 'Other' }
];

const DEFAULT_DOC_TYPE_MAP = {
    VEHICLE: 'LOGBOOK',
    LAND: 'TITLE_DEED',
    PROPERTY: 'TITLE_DEED'
};

const UploadDocumentModal = ({ assetId, assetType, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [documentType, setDocumentType] = useState(DEFAULT_DOC_TYPE_MAP[assetType] || 'OTHER');
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setError('');

        try {
            await assetService.assetDocuments.uploadWithFile(assetId, file, documentType, description);
            onSuccess();
        } catch (err) {
            console.error('Error uploading document:', err);
            setError(err.message || 'Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Upload Document</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                )}

                <div className="space-y-4">
                    {/* Drag and Drop Area */}
                    <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
                            dragActive
                                ? 'border-blue-500 bg-blue-50'
                                : file
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-300 hover:border-gray-400'
                        }`}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept="image/*,.pdf,.doc,.docx"
                        />
                        {file ? (
                            <div>
                                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                    className="mt-2 text-xs text-red-600 hover:text-red-700"
                                >
                                    Remove file
                                </button>
                            </div>
                        ) : (
                            <div>
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-600">
                                    Drag and drop a file here, or click to browse
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Supports images, PDF, DOC files
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Document Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Type
                        </label>
                        <select
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {DOCUMENT_TYPE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="2"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Brief description of the document..."
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-6">
                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4" />
                                Upload
                            </>
                        )}
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssetDetailsView;
